import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, Modal, Spinner } from 'react-bootstrap';
import {
  FaArrowLeft,
  FaSearch,
  FaUserTie,
  FaEdit,
  FaCheck,
  FaTimes,
  FaTrash,
  FaExclamationTriangle,
} from 'react-icons/fa';
import trabajadoresMantenimientoApi from '../../../services/api/trabajadoresMantenimientoApi';

const emptyForm = () => ({
  nombre_completo: '',
  numero_documento_identidad: '',
  cargo: '',
  area: '',
  telefono: '',
  localia: '',
  departamento: '',
  centro_costo: '',
  activo: true,
});

const MantenimientoTrabajadoresHoteleria = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');
    if (!selectedProject?.id) {
      toast.error('Seleccione un proyecto');
      return;
    }
    setLoading(true);
    try {
      const res = await trabajadoresMantenimientoApi.listar({
        page,
        page_size: pageSize,
        q: q || undefined,
      });
      const payload = res.data?.data;
      if (res.data?.success && payload) {
        setItems(payload.items || []);
        setTotal(payload.total ?? 0);
      } else {
        toast.error(res.data?.message || 'No se pudo cargar la lista');
      }
    } catch (e) {
      const d = e.response?.data?.detail ?? e.response?.data?.message ?? e.message;
      toast.error(typeof d === 'string' ? d : 'Error al cargar trabajadores');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const openEdit = async (row) => {
    setEditId(row.id);
    setForm(emptyForm());
    setShowModal(true);
    setLoadingDetail(true);
    try {
      const res = await trabajadoresMantenimientoApi.obtener(row.id);
      const d = res.data?.data;
      if (res.data?.success && d) {
        setForm({
          nombre_completo: d.nombre_completo || '',
          numero_documento_identidad: d.numero_documento_identidad || '',
          cargo: d.cargo || '',
          area: d.area || '',
          telefono: d.telefono || '',
          localia: d.localia || '',
          departamento: d.departamento || '',
          centro_costo: d.centro_costo || '',
          activo: Boolean(d.activo),
        });
      } else {
        toast.error('No se pudo cargar el trabajador');
        setShowModal(false);
      }
    } catch (e) {
      const msg = e.response?.data?.detail ?? e.message;
      toast.error(typeof msg === 'string' ? msg : 'Error al cargar');
      setShowModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(emptyForm());
  };

  const openDeleteConfirm = (row) => {
    setPendingDelete(row);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  const executeDelete = async () => {
    if (!pendingDelete) return;
    const row = pendingDelete;
    setDeletingId(row.id);
    try {
      const res = await trabajadoresMantenimientoApi.eliminar(row.id);
      if (res.data?.success) {
        toast.success(res.data?.message || 'Trabajador eliminado');
        setShowDeleteModal(false);
        setPendingDelete(null);
        if (items.length === 1 && page > 1) {
          setPage((p) => Math.max(1, p - 1));
        } else {
          load();
        }
      } else {
        toast.error(res.data?.message || 'No se pudo eliminar');
      }
    } catch (e) {
      const d = e.response?.data?.detail ?? e.response?.data?.message ?? e.message;
      toast.error(typeof d === 'string' ? d : 'Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSave = async () => {
    if (!editId) return;
    setSaving(true);
    try {
      const body = {
        nombre_completo: form.nombre_completo || undefined,
        numero_documento_identidad: form.numero_documento_identidad || undefined,
        cargo: form.cargo || undefined,
        area: form.area || undefined,
        telefono: form.telefono || undefined,
        localia: form.localia || undefined,
        departamento: form.departamento || undefined,
        centro_costo: form.centro_costo || undefined,
        activo: form.activo,
      };
      const res = await trabajadoresMantenimientoApi.actualizar(editId, body);
      if (res.data?.success) {
        toast.success(res.data?.message || 'Guardado');
        closeModal();
        load();
      } else {
        toast.error(res.data?.message || 'No se pudo guardar');
      }
    } catch (e) {
      const d = e.response?.data?.detail ?? e.response?.data?.message ?? e.message;
      toast.error(typeof d === 'string' ? d : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          .dh-main-title { font-size: 2.2rem; font-weight: 700; margin: 0; }
          .dh-main-subtitle { font-size: 1.1rem; opacity: 0.9; margin: 0.5rem 0 0 0; }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-primary:hover { color: white; filter: brightness(1.05); }
          .btn-dh-edit {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            box-shadow: 0 2px 10px rgba(102, 126, 234, 0.35);
          }
          .btn-dh-edit:hover { color: white; filter: brightness(1.06); }
          .btn-dh-danger-outline {
            background: white;
            border: 2px solid #ef4444;
            color: #dc2626;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          .btn-dh-danger-outline:hover:not(:disabled) {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            border-color: transparent;
          }
          .dh-modal-delete-content {
            border: none;
            border-radius: 18px;
            overflow: hidden;
            box-shadow: 0 22px 55px rgba(15, 23, 42, 0.22);
          }
          .dh-modal-delete-header {
            background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
            color: white;
            padding: 1.15rem 1.35rem;
            border-bottom: none;
          }
          .dh-modal-delete-header .btn-close {
            filter: invert(1);
            opacity: 0.9;
          }
          .dh-modal-delete-body {
            padding: 1.5rem 1.35rem 1.25rem;
            background: linear-gradient(180deg, #fafafa 0%, #fff 40%);
          }
          .dh-modal-delete-highlight {
            font-weight: 700;
            color: #1f2937;
            font-size: 1.05rem;
          }
          .btn-dh-danger-solid {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);
          }
          .btn-dh-danger-solid:hover:not(:disabled) {
            color: white;
            filter: brightness(1.05);
          }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 18px;
            font-weight: 600;
          }
          .btn-dh-secondary:hover { color: white; }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 10px 14px;
            transition: all 0.3s ease;
          }
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.8rem !important;
            text-transform: uppercase !important;
            padding: 0.85rem 0.65rem !important;
            border: none !important;
          }
          .dh-enhanced-table tbody td {
            padding: 0.65rem;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.04) 100%) !important;
          }
          .dh-badge-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            border-radius: 10px;
            padding: 4px 10px;
            font-weight: 600;
            font-size: 0.8rem;
          }
          .dh-badge-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            border-radius: 10px;
            padding: 4px 10px;
            font-weight: 600;
            font-size: 0.8rem;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <h1 className="dh-main-title">
                  <FaUserTie className="me-3" />
                  Trabajadores
                </h1>
                <p className="dh-main-subtitle">Mantenimiento de trabajadores del proyecto (Hotelería)</p>
              </div>
              <Button className="btn-dh-secondary d-flex align-items-center" onClick={() => navigate('/home')}>
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <span>Listado</span>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <div className="position-relative" style={{ minWidth: 220 }}>
                      <Form.Control
                        className="dh-form-control"
                        placeholder="Buscar DNI o nombre…"
                        value={qInput}
                        onChange={(e) => setQInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (setPage(1), setQ(qInput.trim()))}
                        style={{ paddingLeft: '2.25rem' }}
                      />
                      <FaSearch
                        className="position-absolute text-muted"
                        style={{ left: '0.75rem', top: '50%', transform: 'translateY(-50%)' }}
                      />
                    </div>
                    <Button
                      className="btn-dh-primary"
                      onClick={() => {
                        setPage(1);
                        setQ(qInput.trim());
                      }}
                    >
                      Buscar
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '1.5rem' }}>
                  <div className="table-responsive" style={{ maxHeight: 560, overflowY: 'auto' }}>
                    <Table className="dh-enhanced-table mb-0">
                      <thead>
                        <tr>
                          <th>DNI</th>
                          <th>Nombre</th>
                          <th>Cargo</th>
                          <th>Área</th>
                          <th>Cliente</th>
                          <th className="text-center">Estado</th>
                          <th className="text-end">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={7} className="text-center py-5">
                              <Spinner animation="border" size="sm" className="me-2" />
                              Cargando…
                            </td>
                          </tr>
                        ) : items.length === 0 ? (
                          <tr>
                            <td colSpan={7}>
                              <Alert variant="info" className="mb-0">
                                No hay trabajadores para mostrar.
                              </Alert>
                            </td>
                          </tr>
                        ) : (
                          items.map((row) => (
                            <tr key={row.id}>
                              <td className="text-nowrap">{row.numero_documento_identidad || '—'}</td>
                              <td style={{ fontWeight: 600, color: '#374151' }}>{row.nombre_completo || '—'}</td>
                              <td>{row.cargo || '—'}</td>
                              <td>{row.area || '—'}</td>
                              <td className="small text-muted">{row.cliente_nombre || '—'}</td>
                              <td className="text-center">
                                {row.activo ? (
                                  <Badge className="dh-badge-success">
                                    <FaCheck className="me-1" />
                                    Activo
                                  </Badge>
                                ) : (
                                  <Badge className="dh-badge-danger">
                                    <FaTimes className="me-1" />
                                    Inactivo
                                  </Badge>
                                )}
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex gap-1 flex-wrap justify-content-end">
                                  <Button
                                    size="sm"
                                    className="btn-dh-edit"
                                    onClick={() => openEdit(row)}
                                    disabled={deletingId === row.id}
                                  >
                                    <FaEdit className="me-1" />
                                    Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="btn-dh-danger-outline"
                                    onClick={() => openDeleteConfirm(row)}
                                    disabled={deletingId === row.id}
                                  >
                                    {deletingId === row.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <>
                                        <FaTrash className="me-1" />
                                        Eliminar
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <span className="text-muted small">
                      Total: {total} — Página {page} de {totalPages}
                    </span>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={page <= 1 || loading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={page >= totalPages || loading}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserTie className="me-2" />
            Editar trabajador
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetail ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>DNI / documento</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.numero_documento_identidad}
                      onChange={(e) => setForm({ ...form, numero_documento_identidad: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Nombre completo</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.nombre_completo}
                      onChange={(e) => setForm({ ...form, nombre_completo: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Cargo</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.cargo}
                      onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Área</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.area}
                      onChange={(e) => setForm({ ...form, area: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.telefono}
                      onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Localidad</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.localia}
                      onChange={(e) => setForm({ ...form, localia: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Departamento</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.departamento}
                      onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Centro de costo</Form.Label>
                    <Form.Control
                      className="dh-form-control"
                      value={form.centro_costo}
                      onChange={(e) => setForm({ ...form, centro_costo: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Check
                    type="switch"
                    id="activo-trab"
                    label="Activo"
                    checked={form.activo}
                    onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                  />
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving || loadingDetail}>
            Cancelar
          </Button>
          <Button className="btn-dh-primary" onClick={handleSave} disabled={saving || loadingDetail}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={closeDeleteModal}
        centered
        backdrop={deletingId ? 'static' : true}
        keyboard={!deletingId}
        contentClassName="dh-modal-delete-content"
      >
        <Modal.Header className="dh-modal-delete-header" closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 text-white fs-5 mb-0">
            <FaExclamationTriangle className="flex-shrink-0" />
            Confirmar eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="dh-modal-delete-body border-0">
          <p className="mb-2 text-secondary small text-uppercase fw-semibold" style={{ letterSpacing: '0.04em' }}>
            Trabajador a eliminar
          </p>
          <p className="dh-modal-delete-highlight mb-3">
            {pendingDelete
              ? pendingDelete.nombre_completo ||
                pendingDelete.numero_documento_identidad ||
                `ID ${pendingDelete.id}`
              : '—'}
          </p>
          {pendingDelete?.numero_documento_identidad && pendingDelete?.nombre_completo ? (
            <p className="text-muted small mb-2">
              DNI: <span className="fw-semibold text-dark">{pendingDelete.numero_documento_identidad}</span>
            </p>
          ) : null}
          <Alert variant="light" className="border mb-0 small" style={{ borderColor: '#fecaca', background: '#fff7ed' }}>
            <span className="text-dark">
              Solo se puede eliminar si no hay <strong>reservas</strong>, <strong>check-in</strong> ni{' '}
              <strong>check-out</strong> en hotelería. Esta acción no se puede deshacer.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 bg-white px-3 pb-3">
          <Button variant="outline-secondary" onClick={closeDeleteModal} disabled={!!deletingId}>
            Cancelar
          </Button>
          <Button className="btn-dh-danger-solid" onClick={executeDelete} disabled={!!deletingId}>
            {deletingId ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Eliminando…
              </>
            ) : (
              <>
                <FaTrash className="me-2" />
                Sí, eliminar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MantenimientoTrabajadoresHoteleria;
